import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddOutlined,
  CheckCircleOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import { useMountEffect, useTableState } from 'shared/hooks';
import { adminActions } from 'features/admin/store/slice';
import api from 'api';
import sortFields from 'api/sortFields';
import { getTableHeaders, prepareTableData } from 'utils/tables';
import { formatDateToStr } from 'utils/dates';
import { isProdEnvironment } from 'utils/constants';
import colors from 'styles/colors';

import PaginatedTable from 'shared/components/data/PaginatedTable/PaginatedTable';
import LoadingSpinner from 'shared/components/feedback/LoadingSpinner/LoadingSpinner';
import ActionButton from 'shared/components/buttons/ActionButton/ActionButton';
import Hl7VisitForm from 'features/admin/components/Hl7VisitForm';
import { mapApi2Form } from 'features/admin/components/Hl7VisitForm/utils';
import { FlexBoxColumn, FlexBoxSpaceBetween, Box } from 'styles/layout';

const buildTableColumns = ({ onAddInsurance }) => [
  {
    header: {
      id: 'givenName',
      value: 'Given Name',
    },
    dataKey: 'givenName',
    width: '12%',
  },
  {
    header: {
      id: 'familyName',
      value: 'Family Name',
    },
    dataKey: 'familyName',
    width: '12%',
  },
  {
    header: {
      id: 'dob',
      value: 'Date of Birth',
    },
    formatValue: ({ dob }) => (dob ? formatDateToStr(dob) : '-'),
    width: '12%',
  },
  {
    header: {
      id: sortFields.hl7Visits.patientAccountNumber,
      value: 'Patient Account Number',
      isSortable: true,
    },
    dataKey: 'patientAccountNumber',
    width: '18%',
  },
  {
    header: {
      id: sortFields.hl7Visits.admitTimestamp,
      value: 'Admit Date',
      isSortable: true,
    },
    formatValue: ({ admitTimestamp }) =>
      admitTimestamp ? formatDateToStr(admitTimestamp) : '-',
    width: '15%',
  },
  {
    header: {
      id: sortFields.hl7Visits.dischargeTimestamp,
      value: 'Discharge Date',
      isSortable: true,
    },
    formatValue: ({ dischargeTimestamp }) =>
      dischargeTimestamp ? formatDateToStr(dischargeTimestamp) : '-',
    width: '15%',
  },
  {
    header: {
      id: sortFields.hl7Visits.patientClass,
      value: 'Patient Class',
      isSortable: true,
    },
    dataKey: 'patientClass',
    width: '10%',
  },
  {
    header: {
      id: sortFields.hl7Visits.accountStatus,
      value: 'Status',
      isSortable: true,
    },
    dataKey: 'accountStatus',
    width: '10%',
  },
  {
    header: {
      id: 'hasInsurance',
      value: 'Insurance',
    },
    width: '160px',
    align: 'center',
    formatValue: ({ id, hasInsurance }) =>
      hasInsurance ? (
        <CheckCircleOutlined fontSize="small" sx={{ color: colors.success }} />
      ) : (
        <FlexBoxColumn alignItems="center" gap={0.5}>
          <CancelOutlined
            fontSize="small"
            sx={{ color: colors.notification }}
          />
          {onAddInsurance && (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                onAddInsurance(id);
              }}
            >
              Add Insurance Info
            </Button>
          )}
        </FlexBoxColumn>
      ),
  },
];

const Hl7VisitsListTab = ({ hl7PatientId, patientData }) => {
  const dispatch = useDispatch();
  const { data: visits, pageCount } = useSelector(
    ({ admin }) => admin.hl7Visits,
  );

  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedVisitId, setSelectedVisitId] = React.useState(null);
  const [selectedVisit, setSelectedVisit] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    page,
    sortBy,
    sortOrder,
    handlePageChange,
    handleSortChange,
  } = useTableState(sortFields.hl7Visits.dischargeTimestamp);

  const fetchVisits = React.useCallback(() => {
    if (!hl7PatientId) return;
    dispatch(
      adminActions.listHl7Visits({
        page,
        sortBy,
        sortOrder,
        filters: { Hl7PatientId: hl7PatientId },
      }),
    );
  }, [dispatch, page, sortBy, sortOrder, hl7PatientId]);

  useMountEffect(fetchVisits);

  React.useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const fetchVisitDetails = React.useCallback(async (visitId) => {
    const { data } = await api.getHl7Visit(visitId);
    setSelectedVisit(data);
  }, []);

  const handleAddVisit = React.useCallback(() => {
    setSelectedVisitId(null);
    setSelectedVisit(null);
    setModalOpen(true);
  }, []);

  const handleRowClick = React.useCallback(
    async (visitId) => {
      if (isProdEnvironment) return;
      setSelectedVisitId(visitId);
      setSelectedVisit(null);
      setModalOpen(true);
      await fetchVisitDetails(visitId);
    },
    [fetchVisitDetails],
  );

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false);
    setSelectedVisitId(null);
    setSelectedVisit(null);
  }, []);

  const handleAddInsurance = React.useCallback(
    async (visitId) => {
      const result = await dispatch(
        adminActions.addHl7VisitInsurance(visitId),
      );
      if (adminActions.addHl7VisitInsurance.fulfilled.match(result)) {
        if (modalOpen && selectedVisitId === visitId) {
          await fetchVisitDetails(visitId);
        }
        fetchVisits();
      }
    },
    [
      dispatch,
      fetchVisitDetails,
      fetchVisits,
      modalOpen,
      selectedVisitId,
    ],
  );

  const handleSubmit = React.useCallback(
    async (values) => {
      setIsSubmitting(true);
      const payload = {
        hl7PatientId: parseInt(hl7PatientId, 10),
        ...values,
      };

      const result = selectedVisitId
        ? await dispatch(
            adminActions.updateHl7Visit({
              id: selectedVisitId,
              ...payload,
            }),
          )
        : await dispatch(adminActions.createHl7Visit(payload));

      setIsSubmitting(false);

      if (
        adminActions.updateHl7Visit.fulfilled.match(result) ||
        adminActions.createHl7Visit.fulfilled.match(result)
      ) {
        handleCloseModal();
        fetchVisits();
      }
    },
    [dispatch, hl7PatientId, selectedVisitId, handleCloseModal, fetchVisits],
  );

  if (!visits) return <LoadingSpinner />;

  const tableColumns = buildTableColumns({
    onAddInsurance: !isProdEnvironment ? handleAddInsurance : null,
  });

  // Enrich visits data with patient information
  const enrichedVisits = visits.map((visit) => ({
    ...visit,
    givenName: patientData?.givenName || '-',
    familyName: patientData?.familyName || '-',
    dob: patientData?.dob || null,
  }));

  return (
    <FlexBoxColumn p={2}>
      {!isProdEnvironment && (
        <FlexBoxSpaceBetween mb={2}>
          <Box />
          <ActionButton startIcon={<AddOutlined />} onClick={handleAddVisit}>
            Add New Visit
          </ActionButton>
        </FlexBoxSpaceBetween>
      )}

      <PaginatedTable
        headings={getTableHeaders(tableColumns)}
        data={prepareTableData(enrichedVisits, tableColumns)}
        fallbackMsg="No visits found for this patient"
        sortBy={sortBy}
        sortOrder={sortOrder}
        currentPage={page}
        totalPages={pageCount}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onClickRow={!isProdEnvironment ? handleRowClick : undefined}
      />

      {!isProdEnvironment && (
        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle>{selectedVisitId ? 'Edit HL7 Visit' : 'Add HL7 Visit'}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Hl7VisitForm
              key={selectedVisitId || 'new'}
              initValues={mapApi2Form(selectedVisit)}
              onSubmit={handleSubmit}
              onCancel={handleCloseModal}
              onAddInsurance={handleAddInsurance}
              hideButtons
              disabled={isSubmitting}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button
              form="hl7VisitForm"
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting}
            >
              {selectedVisitId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </FlexBoxColumn>
  );
};

Hl7VisitsListTab.propTypes = {
  hl7PatientId: PropTypes.string.isRequired,
  patientData: PropTypes.shape({
    givenName: PropTypes.string,
    familyName: PropTypes.string,
    dob: PropTypes.string,
  }),
};

export default Hl7VisitsListTab;
